import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

const STORAGE_KEY = "ekselencia_visitor_segment";

type VisitorType = "seeker" | "professional";

interface Category {
  id: string;
  emoji: string;
  label: string;
  seekerPath: string;
  professionalPath: string;
}

const CATEGORIES: Category[] = [
  { id: "trainer", emoji: "🏋️", label: "Entrenadores Personales", seekerPath: "/trainers", professionalPath: "/become-trainer" },
  { id: "physio", emoji: "💆", label: "Fisioterapeutas", seekerPath: "/fisioterapeutas", professionalPath: "/become-physiotherapist" },
  { id: "nutritionist", emoji: "🥗", label: "Nutricionistas", seekerPath: "/nutricionistas", professionalPath: "/become-nutritionist" },
  // Nota: en el router no existen rutas raíz tipo /yoga o /crossfit (solo /yoga/:slug, etc.).
  // Para “busco…”, mandamos a las landing pages; para “soy profesional”, a los funnels.
  { id: "yoga", emoji: "🧘", label: "Estudios de Yoga", seekerPath: "/landing-yoga", professionalPath: "/funnel/yoga" },
  { id: "crossfit", emoji: "💪", label: "Boxes de CrossFit", seekerPath: "/landing-crossfit", professionalPath: "/funnel/crossfit" },
  { id: "gym", emoji: "🏢", label: "Gimnasios", seekerPath: "/landing-fitness", professionalPath: "/funnel/gym" },
  { id: "pilates", emoji: "🤸", label: "Centros de Pilates", seekerPath: "/landing-pilates", professionalPath: "/funnel/pilates" },
  { id: "dance", emoji: "💃", label: "Academias de Baile", seekerPath: "/landing-dance", professionalPath: "/funnel/dance" },
  { id: "martial", emoji: "🥋", label: "Artes Marciales", seekerPath: "/landing-martial-arts", professionalPath: "/funnel/martial-arts" },
];

export const VisitorSegmentationModal = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);
  const [visitorType, setVisitorType] = useState<VisitorType | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  useEffect(() => {
    // Check if already segmented
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      // Small delay to let the page render first
      const timer = setTimeout(() => setOpen(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleVisitorTypeSelect = (type: VisitorType) => {
    setVisitorType(type);
    setStep(2);
  };

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId);
  };

  const handleConfirm = () => {
    if (!visitorType || !selectedCategory) return;

    const category = CATEGORIES.find(c => c.id === selectedCategory);
    if (!category) return;

    // Store the selection
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      type: visitorType,
      category: selectedCategory,
      timestamp: Date.now()
    }));

    setOpen(false);

    // Navigate to appropriate page
    const path = visitorType === "seeker" ? category.seekerPath : category.professionalPath;
    navigate(path);
  };

  const handleSkip = () => {
    // Store that they skipped but don't redirect
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      type: "skipped",
      timestamp: Date.now()
    }));
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) handleSkip();
    }}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl text-center">
            {step === 1 ? "¡Bienvenido a Ekselencia!" : "¿Qué te interesa?"}
          </DialogTitle>
          <DialogDescription className="text-center text-base">
            {step === 1 
              ? "Cuéntanos quién eres para personalizar tu experiencia"
              : visitorType === "seeker" 
                ? "Selecciona qué tipo de profesional o centro buscas"
                : "Selecciona tu tipo de negocio o servicio"
            }
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <div className="grid gap-4 py-4">
            <Button
              variant="outline"
              size="lg"
              className="h-auto py-6 flex flex-col gap-2 hover:border-primary hover:bg-primary/5 transition-all"
              onClick={() => handleVisitorTypeSelect("seeker")}
            >
              <span className="text-3xl">🔍</span>
              <span className="text-lg font-semibold">Estoy buscando...</span>
              <span className="text-sm text-muted-foreground">
                Quiero encontrar profesionales, gimnasios o centros
              </span>
            </Button>

            <Button
              variant="outline"
              size="lg"
              className="h-auto py-6 flex flex-col gap-2 hover:border-accent hover:bg-accent/5 transition-all"
              onClick={() => handleVisitorTypeSelect("professional")}
            >
              <span className="text-3xl">💼</span>
              <span className="text-lg font-semibold">Soy profesional</span>
              <span className="text-sm text-muted-foreground">
                Quiero promocionar mis servicios y captar clientes
              </span>
            </Button>
          </div>
        ) : (
          <div className="py-4">
            <RadioGroup
              value={selectedCategory || ""}
              onValueChange={handleCategorySelect}
              className="grid grid-cols-1 gap-2"
            >
              {CATEGORIES.map((category) => (
                <Label
                  key={category.id}
                  htmlFor={category.id}
                  className={cn(
                    "flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all",
                    selectedCategory === category.id
                      ? "border-primary bg-primary/10"
                      : "border-border hover:border-primary/50 hover:bg-muted/50"
                  )}
                >
                  <RadioGroupItem value={category.id} id={category.id} />
                  <span className="text-xl">{category.emoji}</span>
                  <span className="font-medium">{category.label}</span>
                </Label>
              ))}
            </RadioGroup>

            <div className="flex gap-3 mt-6">
              <Button
                variant="outline"
                onClick={() => setStep(1)}
                className="flex-1"
              >
                Atrás
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={!selectedCategory}
                className="flex-1"
              >
                Continuar
              </Button>
            </div>
          </div>
        )}

        <button
          onClick={handleSkip}
          className="text-xs text-muted-foreground hover:text-foreground text-center w-full mt-2 underline"
        >
          Omitir por ahora
        </button>
      </DialogContent>
    </Dialog>
  );
};

export default VisitorSegmentationModal;
